/**
 * EMVCo Merchant-Presented QR primitives — the encoding KHQR is built on.
 *
 * The format is length-prefixed TLV in ASCII:
 *
 *   ID (2 chars) | LENGTH (2 chars, zero-padded) | VALUE (LENGTH chars)
 *
 * and the whole payload ends with a CRC over everything up to and including
 * the CRC's own "6304" header. Getting the CRC wrong produces a QR that scans
 * but is rejected by the bank app — which looks like our bug, not the
 * customer's, so this is tested against known vectors.
 */

export interface TlvField {
  id: string;
  value: string;
}

export function encodeTlv(id: string, value: string): string {
  if (id.length !== 2) {
    throw new RangeError(`EMV tag id must be 2 characters, got "${id}"`);
  }
  if (value.length > 99) {
    throw new RangeError(`EMV value for tag ${id} exceeds 99 characters (${value.length})`);
  }
  return `${id}${String(value.length).padStart(2, '0')}${value}`;
}

export function encodeFields(fields: readonly TlvField[]): string {
  return fields.map((field) => encodeTlv(field.id, field.value)).join('');
}

/** Nested templates (tags 29/30/62/99) carry TLV inside their value. */
export function encodeTemplate(id: string, fields: readonly TlvField[]): string {
  return encodeTlv(id, encodeFields(fields));
}

export function parseTlv(payload: string): TlvField[] {
  const fields: TlvField[] = [];
  let cursor = 0;
  while (cursor < payload.length) {
    if (cursor + 4 > payload.length) {
      throw new SyntaxError(`Truncated EMV field at offset ${cursor}`);
    }
    const id = payload.slice(cursor, cursor + 2);
    const lengthText = payload.slice(cursor + 2, cursor + 4);
    const length = Number.parseInt(lengthText, 10);
    if (!/^\d{2}$/.test(lengthText) || Number.isNaN(length)) {
      throw new SyntaxError(`Invalid EMV length "${lengthText}" at offset ${cursor + 2}`);
    }
    const start = cursor + 4;
    const end = start + length;
    if (end > payload.length) {
      throw new SyntaxError(`EMV field ${id} claims ${length} chars but the payload ends early`);
    }
    fields.push({ id, value: payload.slice(start, end) });
    cursor = end;
  }
  return fields;
}

export function findField(fields: readonly TlvField[], id: string): string | undefined {
  return fields.find((field) => field.id === id)?.value;
}

/**
 * CRC-16/CCITT-FALSE: polynomial 0x1021, initial value 0xFFFF, no input or
 * output reflection, no final XOR. This is the variant EMVCo mandates.
 */
export function crc16(input: string): number {
  let crc = 0xffff;
  for (let i = 0; i < input.length; i += 1) {
    crc ^= input.charCodeAt(i) << 8;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }
  return crc & 0xffff;
}

export const CRC_TAG = '63';
const CRC_HEADER = `${CRC_TAG}04`;

/** Append the 4-hex-digit CRC that closes an EMVCo payload. */
export function appendCrc(payloadWithoutCrc: string): string {
  const base = `${payloadWithoutCrc}${CRC_HEADER}`;
  return `${base}${crc16(base).toString(16).toUpperCase().padStart(4, '0')}`;
}

export function verifyCrc(payload: string): boolean {
  const index = payload.lastIndexOf(CRC_HEADER);
  if (index === -1 || index + 8 !== payload.length) return false;
  const base = payload.slice(0, index + CRC_HEADER.length);
  const expected = crc16(base).toString(16).toUpperCase().padStart(4, '0');
  return payload.slice(index + CRC_HEADER.length).toUpperCase() === expected;
}

/**
 * Bank apps render the merchant name from a limited character set; anything
 * outside ASCII risks a mojibake payee line, which is exactly the moment a
 * customer aborts a transfer. Khmer shop names therefore need a Latin
 * `merchant_name` in the KHQR config, and we defend that here.
 */
export function sanitizeEmvText(text: string, maxLength: number): string {
  const ascii = text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7e]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return ascii.slice(0, maxLength);
}
